import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { invoiceApi, clientApi, projectApi, estimateApi, leadApi, changeOrderApi, timeApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Plus, Minus, Trash2, Save, Send, Eye, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const PAYMENT_TERMS = [
  'Due on Receipt',
  'Net 7',
  'Net 15',
  'Net 30',
  'Net 45',
  'Net 60',
];

const BILLING_TYPES = [
  { value: 'initial', label: 'Initial Invoice (Deposit)' },
  { value: 'standard', label: 'Standard Invoice' },
  { value: 'progress', label: 'Progress Billing' },
  { value: 'final', label: 'Final Invoice' },
];

// Standard "% of total to bill" presets
const BILL_PCT_PRESETS = ['10', '25', '33.3', '50', '100'];

// Pull an estimate as a single tax-inclusive summary line. The estimate's grand total
// already includes any material tax, so the line is marked NON-taxable (we don't add tax again).
const estimateMeta = (est) => {
  const totals = est.totals || {};
  let amount = totals.grand_total || 0;
  if (!amount || amount <= 0) amount = totals.estimate_total || 0;
  if (!amount || amount <= 0) amount = totals.subtotal || totals.line_items_total || 0;
  const name = est.estimate_name || est.filename || 'Estimate';
  const description = est.estimate_number ? `${name} — Estimate #${est.estimate_number}` : name;
  return { id: est.id, name, description, amount: Math.round(amount * 100) / 100, project_id: est.project_id || '' };
};

export default function InvoiceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const estimateIdParam = searchParams.get('estimate_id');
  const clientIdParam = searchParams.get('client_id');
  const projectIdParam = searchParams.get('project_id');
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clientEstimates, setClientEstimates] = useState([]);
  const [pulledEstimates, setPulledEstimates] = useState([]);
  const [projectChangeOrders, setProjectChangeOrders] = useState([]);
  const [pulledChangeOrders, setPulledChangeOrders] = useState([]);
  const [availableTime, setAvailableTime] = useState([]);
  const [pulledTimeIds, setPulledTimeIds] = useState([]);
  const [billPctCustom, setBillPctCustom] = useState(false);
  const [settings, setSettings] = useState({});
  const [nextNumber, setNextNumber] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('draft');

  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    estimate_id: '',
    items: [{ description: '', quantity: 1, unit_price: '', taxable: true }],
    tax_rate: 0,
    notes: '',
    terms: 'Net 30',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    billing_type: 'standard',
    bill_pct: '',
  });

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load approved change orders whenever the selected project changes
  useEffect(() => {
    const loadCOs = async () => {
      if (!formData.project_id) { setProjectChangeOrders([]); return; }
      try {
        const res = await changeOrderApi.getForProject(formData.project_id);
        setProjectChangeOrders((res.data || []).filter((c) => c.status === 'approved'));
      } catch (e) {
        setProjectChangeOrders([]);
      }
    };
    loadCOs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.project_id]);

  // Add an approved change order to the invoice as a summary line (tax already in its total)
  const addChangeOrder = (co) => {
    if (pulledChangeOrders.find((p) => p.id === co.id)) {
      toast.info('That change order is already on this invoice');
      return;
    }
    const description = `${co.co_number} — ${co.title}`;
    setPulledChangeOrders((prev) => [...prev, { id: co.id, co_number: co.co_number, description }]);
    setFormData((prev) => {
      const onlyEmpty = prev.items.length === 1 && !prev.items[0].description && !Number(prev.items[0].unit_price);
      const line = { description, quantity: 1, unit_price: co.total, taxable: false };
      const items = onlyEmpty ? [line] : [...prev.items, line];
      return { ...prev, items };
    });
  };

  const removeChangeOrder = (coId) => {
    const meta = pulledChangeOrders.find((p) => p.id === coId);
    setPulledChangeOrders((prev) => prev.filter((p) => p.id !== coId));
    setFormData((prev) => {
      let removed = false;
      const filtered = prev.items.filter((it) => {
        if (!removed && meta && it.description === meta.description) { removed = true; return false; }
        return true;
      });
      const items = filtered.length ? filtered : [{ description: '', quantity: 1, unit_price: '', taxable: true }];
      return { ...prev, items };
    });
  };

  // Load this client's unbilled time entries (Solo Builder bundle). Silently no-op if not enabled.
  useEffect(() => {
    const loadTime = async () => {
      if (!formData.client_id) { setAvailableTime([]); return; }
      try {
        const res = await timeApi.getUnbilled({ client_id: formData.client_id });
        setAvailableTime(res.data || []);
      } catch (e) {
        setAvailableTime([]);
      }
    };
    loadTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.client_id]);

  const timeLineDescription = (t) => {
    let d = `${t.activity_type} — ${t.duration_hours} hrs (${t.date})`;
    if (t.notes) d += `: ${t.notes}`;
    return d;
  };

  const addTimeEntry = (t) => {
    if (pulledTimeIds.includes(t.id)) { toast.info('That time entry is already added'); return; }
    setPulledTimeIds((prev) => [...prev, t.id]);
    setFormData((prev) => {
      const onlyEmpty = prev.items.length === 1 && !prev.items[0].description && !Number(prev.items[0].unit_price);
      const line = {
        description: timeLineDescription(t),
        quantity: t.duration_hours || 1,
        unit_price: t.hourly_rate != null ? t.hourly_rate : '',
        taxable: false,
        _timeId: t.id,
      };
      const items = onlyEmpty ? [line] : [...prev.items, line];
      return { ...prev, items };
    });
  };

  const removeTimeEntry = (timeId) => {
    setPulledTimeIds((prev) => prev.filter((x) => x !== timeId));
    setFormData((prev) => {
      const filtered = prev.items.filter((it) => it._timeId !== timeId);
      const items = filtered.length ? filtered : [{ description: '', quantity: 1, unit_price: '', taxable: true }];
      return { ...prev, items };
    });
  };

  // Load all estimates for a client (for the "Pull from Estimate" picker)
  const loadClientEstimates = async (clientId) => {
    if (!clientId) {
      setClientEstimates([]);
      return [];
    }
    try {
      const res = await estimateApi.getForClient(clientId);
      setClientEstimates(res.data || []);
      return res.data || [];
    } catch (error) {
      console.error('Failed to load client estimates:', error);
      setClientEstimates([]);
      return [];
    }
  };

  // Add an estimate to the invoice as a tax-inclusive, non-taxable summary line (supports combining)
  const addEstimate = (est) => {
    if (pulledEstimates.find((p) => p.id === est.id)) {
      toast.info('That estimate is already on this invoice');
      return;
    }
    const meta = estimateMeta(est);
    const next = [...pulledEstimates, meta];
    setPulledEstimates(next);
    setFormData((prev) => {
      const onlyEmpty = prev.items.length === 1 && !prev.items[0].description && !Number(prev.items[0].unit_price);
      const line = { description: meta.description, quantity: 1, unit_price: meta.amount, taxable: false };
      const items = onlyEmpty ? [line] : [...prev.items, line];
      return {
        ...prev,
        items,
        estimate_id: next[0].id,
        project_id: prev.project_id || meta.project_id || '',
      };
    });
  };

  // Remove a pulled estimate (drops its summary line)
  const removeEstimate = (id) => {
    const meta = pulledEstimates.find((p) => p.id === id);
    const next = pulledEstimates.filter((p) => p.id !== id);
    setPulledEstimates(next);
    setFormData((prev) => {
      let removed = false;
      const filtered = prev.items.filter((it) => {
        if (!removed && meta && it.description === meta.description) {
          removed = true;
          return false;
        }
        return true;
      });
      const items = filtered.length ? filtered : [{ description: '', quantity: 1, unit_price: '', taxable: true }];
      return { ...prev, items, estimate_id: next[0]?.id || '' };
    });
  };

  const fetchInitialData = async () => {
    try {
      const [clientsRes, settingsRes, nextNumRes] = await Promise.all([
        clientApi.getAll(),
        invoiceApi.getSettings(),
        invoiceApi.getNextNumber(),
      ]);

      setClients(clientsRes.data);
      setSettings(settingsRes.data || {});
      setNextNumber(nextNumRes.data.next_number);

      // Set default tax rate from settings
      if (!isEdit && settingsRes.data?.default_tax_rate) {
        setFormData((prev) => ({ ...prev, tax_rate: settingsRes.data.default_tax_rate }));
      }

      // If editing, fetch the invoice
      if (isEdit) {
        const invoiceRes = await invoiceApi.getOne(id);
        const invoice = invoiceRes.data;
        setInvoiceStatus(invoice.status || 'draft');
        setFormData({
          client_id: invoice.client_id,
          project_id: invoice.project_id || '',
          estimate_id: invoice.estimate_id || '',
          items: invoice.items.map((i) => ({
            description: i.description,
            quantity: i.quantity,
            unit_price: i.unit_price,
            taxable: i.taxable !== undefined ? i.taxable : true,
          })),
          tax_rate: invoice.tax_rate,
          notes: invoice.notes || '',
          terms: invoice.terms || 'Net 30',
          issue_date: invoice.issue_date,
          due_date: invoice.due_date,
          billing_type: invoice.billing_type || 'standard',
          bill_pct: invoice.bill_pct != null ? String(invoice.bill_pct) : '',
        });
        if (invoice.bill_pct != null && !BILL_PCT_PRESETS.includes(String(invoice.bill_pct))) {
          setBillPctCustom(true);
        }
        if (invoice.client_id) {
          const projectsRes = await projectApi.getAll();
          setProjects(projectsRes.data.filter((p) => p.client_id === invoice.client_id));
        }
        setLoading(false);
        return;
      }

      // New invoice — handle estimate_id / client_id query params (from Project page)
      if (estimateIdParam) {
        try {
          const estRes = await estimateApi.getOne(estimateIdParam);
          const est = estRes.data;
          // Resolve the client from the estimate's project or lead
          let resolvedClientId = clientIdParam || '';
          if (!resolvedClientId && est.project_id) {
            const pr = await projectApi.getOne(est.project_id);
            resolvedClientId = pr.data?.client_id || '';
          }
          if (!resolvedClientId && est.lead_id) {
            const lr = await leadApi.getOne(est.lead_id);
            resolvedClientId = lr.data?.client_id || '';
          }
          if (resolvedClientId) {
            await loadClientEstimates(resolvedClientId);
            const projectsRes = await projectApi.getAll();
            setProjects(projectsRes.data.filter((p) => p.client_id === resolvedClientId));
          }
          setFormData((prev) => ({ ...prev, client_id: resolvedClientId || prev.client_id }));
          addEstimate(est);
          toast.success('Pulled details from estimate — review before saving');
        } catch (e) {
          toast.error('Could not load the estimate');
        }
      } else if (clientIdParam) {
        await handleClientChange(clientIdParam);
        if (projectIdParam) {
          setFormData((prev) => ({ ...prev, project_id: projectIdParam }));
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = async (clientId) => {
    setFormData((prev) => ({ ...prev, client_id: clientId, project_id: '', estimate_id: '' }));
    setPulledEstimates([]);

    if (clientId) {
      try {
        const projectsRes = await projectApi.getAll();
        const clientProjects = projectsRes.data.filter((p) => {
          const client = clients.find((c) => c.id === clientId);
          return client && (p.client_id === clientId || p.client_name === client.name);
        });
        setProjects(clientProjects);
      } catch (error) {
        console.error('Failed to load projects:', error);
      }
      await loadClientEstimates(clientId);
    } else {
      setProjects([]);
      setClientEstimates([]);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData((prev) => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: '', taxable: true }],
    }));
  };

  // Insert a negative "credit / adjustment" line to reduce the invoice total
  // (e.g. for billed work that wasn't performed). Description is the comment box.
  const addAdjustment = () => {
    setFormData((prev) => {
      const line = { description: 'Adjustment / Credit', quantity: 1, unit_price: '', taxable: false };
      const onlyEmpty = prev.items.length === 1 && !prev.items[0].description && !Number(prev.items[0].unit_price);
      const items = onlyEmpty ? [line] : [...prev.items, line];
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) {
      toast.error('Invoice must have at least one item');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce(
      (sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0),
      0
    );
    const taxableSubtotal = formData.items.reduce(
      (sum, item) => sum + (item.taxable ? (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0) : 0),
      0
    );
    const taxAmount = taxableSubtotal * (formData.tax_rate / 100);
    const total = subtotal + taxAmount;
    const pct = parseFloat(formData.bill_pct);
    const amountDue = pct > 0 && pct < 100 ? total * (pct / 100) : total;
    return { subtotal, taxAmount, total, amountDue };
  };

  const handleSubmit = async (mode = 'draft') => {
    // mode: 'draft' (WIP, not counted), 'finalize' (issue, no email), 'send' (issue + email)
    // Validation
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }

    const validItems = formData.items.filter((i) => i.description.trim());
    if (validItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        client_id: formData.client_id,
        project_id: formData.project_id || null,
        items: validItems.map((i) => ({
          description: i.description,
          quantity: parseFloat(i.quantity) || 1,
          unit_price: parseFloat(i.unit_price) || 0,
          taxable: i.taxable !== false,
        })),
        tax_rate: parseFloat(formData.tax_rate) || 0,
        notes: formData.notes || null,
        terms: formData.terms,
        issue_date: formData.issue_date || null,
        due_date: formData.due_date || null,
        billing_type: formData.billing_type || 'standard',
        bill_pct: formData.bill_pct !== '' ? parseFloat(formData.bill_pct) : null,
        estimate_id: formData.estimate_id || (pulledEstimates[0]?.id ?? null),
        estimate_ids: pulledEstimates.length ? pulledEstimates.map((p) => p.id) : null,
      };

      let invoice;
      if (isEdit) {
        const res = await invoiceApi.update(id, payload);
        invoice = res.data;
        toast.success('Invoice updated');
      } else {
        const res = await invoiceApi.create(payload);
        invoice = res.data;
        toast.success(`Invoice ${invoice.invoice_number} created`);
      }

      if (mode === 'send') {
        await invoiceApi.send(invoice.id);
        toast.success(`Invoice sent to ${invoice.client_email}`);
      } else if (mode === 'finalize') {
        await invoiceApi.finalize(invoice.id);
        toast.success('Invoice finalized (not emailed)');
      }

      // Mark any imported time entries as billed against this invoice
      if (pulledTimeIds.length) {
        try { await timeApi.markBilled(pulledTimeIds, invoice.id); } catch (e) { /* non-fatal */ }
      }

      navigate(`/invoices/${invoice.id}`);
    } catch (error) {
      console.error('Failed to save invoice:', error);
      toast.error('Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const { subtotal, taxAmount, total, amountDue } = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoice-form">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEdit ? 'Edit Invoice' : 'New Invoice'}
            </h1>
            {!isEdit && (
              <p className="text-muted-foreground">Next: {nextNumber}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {isEdit && invoiceStatus !== 'draft' ? (
            <>
              <Button variant="outline" onClick={() => handleSubmit('draft')} disabled={saving} data-testid="save-changes-btn">
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
              <Button onClick={() => handleSubmit('send')} disabled={saving} data-testid="save-send-balance-btn">
                <Send className="h-4 w-4 mr-2" />
                {invoiceStatus === 'partial' ? 'Save & Send Balance' : 'Save & Resend'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleSubmit('draft')} disabled={saving} data-testid="save-draft-btn">
                <Save className="h-4 w-4 mr-2" />
                Save as Draft
              </Button>
              <Button variant="outline" onClick={() => handleSubmit('finalize')} disabled={saving} data-testid="save-finalize-btn">
                <Check className="h-4 w-4 mr-2" />
                Save &amp; Don't Send
              </Button>
              <Button onClick={() => handleSubmit('send')} disabled={saving} data-testid="save-send-btn">
                <Send className="h-4 w-4 mr-2" />
                Save &amp; Send
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client & Project */}
          <Card>
            <CardHeader>
              <CardTitle>Client Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Client *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={handleClientChange}
                >
                  <SelectTrigger data-testid="client-select">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name || `${client.first_name} ${client.last_name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {projects.length > 0 && (
                <div>
                  <Label>Project (Optional)</Label>
                  <Select
                    value={formData.project_id || 'none'}
                    onValueChange={(v) => setFormData((prev) => ({ ...prev, project_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project</SelectItem>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.project_id && projectChangeOrders.length > 0 && (
                <div>
                  <Label>Add Change Order / T&M (Optional)</Label>
                  <Select
                    value="none"
                    onValueChange={(v) => {
                      if (v === 'none') return;
                      const co = projectChangeOrders.find((c) => c.id === v);
                      if (co) addChangeOrder(co);
                    }}
                  >
                    <SelectTrigger data-testid="change-order-select">
                      <SelectValue placeholder="Add an approved change order" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select a change order…</SelectItem>
                      {projectChangeOrders
                        .filter((c) => !pulledChangeOrders.find((p) => p.id === c.id))
                        .map((co) => (
                          <SelectItem key={co.id} value={co.id}>
                            {co.co_number} — {co.title} ({formatCurrency(co.total)})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {pulledChangeOrders.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="pulled-change-orders">
                      {pulledChangeOrders.map((p) => (
                        <span
                          key={p.id}
                          className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-full px-2 py-1"
                          data-testid={`pulled-co-${p.id}`}
                        >
                          {p.co_number}
                          <button type="button" onClick={() => removeChangeOrder(p.id)} className="hover:text-amber-950">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {formData.client_id && availableTime.length > 0 && (
                <div>
                  <Label>Import Unbilled Time (Optional)</Label>
                  <Select
                    value="none"
                    onValueChange={(v) => {
                      if (v === 'none') return;
                      const t = availableTime.find((x) => x.id === v);
                      if (t) addTimeEntry(t);
                    }}
                  >
                    <SelectTrigger data-testid="time-import-select">
                      <SelectValue placeholder="Add a logged time entry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select unbilled time…</SelectItem>
                      {availableTime
                        .filter((t) => !pulledTimeIds.includes(t.id))
                        .map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.date} · {t.activity_type} · {t.duration_hours} hrs
                            {t.hourly_rate ? ` @ ${formatCurrency(t.hourly_rate)}` : ''}
                            {t.is_billable ? '' : ' (non-billable)'}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Each entry becomes an editable line item — adjust the wording and amount before sending so the client sees exactly what you want.
                  </p>
                  {pulledTimeIds.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="pulled-time-entries">
                      {availableTime.filter((t) => pulledTimeIds.includes(t.id)).map((t) => (
                        <span
                          key={t.id}
                          className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded-full px-2 py-1"
                          data-testid={`pulled-time-${t.id}`}
                        >
                          {t.activity_type} · {t.duration_hours}h
                          <button type="button" onClick={() => removeTimeEntry(t.id)} className="hover:text-blue-950">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}


              {formData.client_id && clientEstimates.length > 0 && (
                <div>
                  <Label>Add from Estimate (Optional)</Label>
                  <Select
                    value="none"
                    onValueChange={(v) => {
                      if (v === 'none') return;
                      const est = clientEstimates.find((e) => e.id === v);
                      if (est) addEstimate(est);
                    }}
                  >
                    <SelectTrigger data-testid="estimate-select">
                      <SelectValue placeholder="Add an estimate to this invoice" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select an estimate…</SelectItem>
                      {clientEstimates
                        .filter((e) => !pulledEstimates.find((p) => p.id === e.id))
                        .map((est) => (
                          <SelectItem key={est.id} value={est.id}>
                            {(est.estimate_name || est.filename)}
                            {est.totals?.grand_total ? ` — ${formatCurrency(est.totals.grand_total)}` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {pulledEstimates.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="pulled-estimates">
                      {pulledEstimates.map((p) => (
                        <span
                          key={p.id}
                          className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded-full px-2 py-1"
                          data-testid={`pulled-estimate-${p.id}`}
                        >
                          {p.name}
                          <button
                            type="button"
                            onClick={() => removeEstimate(p.id)}
                            className="hover:text-blue-950"
                            data-testid={`remove-estimate-${p.id}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Each estimate adds a summary line at its grand total. Add multiple to combine them onto one invoice. Edit or add lines below.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Billing Type & Percentage */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Billing Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select
                    value={formData.billing_type}
                    onValueChange={(v) => setFormData((prev) => ({
                      ...prev,
                      billing_type: v,
                      // Initial invoice defaults to a 50% deposit (editable)
                      bill_pct: v === 'initial' && prev.bill_pct === '' ? '50' : prev.bill_pct,
                    }))}
                  >
                    <SelectTrigger data-testid="billing-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_TYPES.map((bt) => (
                        <SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>% of Total to Bill</Label>
                  <Select
                    value={billPctCustom ? 'custom' : (formData.bill_pct || '100')}
                    onValueChange={(v) => {
                      if (v === 'custom') {
                        setBillPctCustom(true);
                      } else {
                        setBillPctCustom(false);
                        setFormData((prev) => ({ ...prev, bill_pct: v === '100' ? '' : v }));
                      }
                    }}
                  >
                    <SelectTrigger data-testid="bill-pct-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="25">25%</SelectItem>
                      <SelectItem value="33.3">33.3% (Thirds)</SelectItem>
                      <SelectItem value="50">50%</SelectItem>
                      <SelectItem value="100">100% (Full)</SelectItem>
                      <SelectItem value="custom">Custom %</SelectItem>
                    </SelectContent>
                  </Select>
                  {billPctCustom && (
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={formData.bill_pct}
                        onChange={(e) => setFormData((prev) => ({ ...prev, bill_pct: e.target.value }))}
                        placeholder="e.g. 40"
                        className="w-24"
                        data-testid="bill-pct-input"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  )}
                </div>
              </div>
              {parseFloat(formData.bill_pct) > 0 && parseFloat(formData.bill_pct) < 100 && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-sm text-sm" data-testid="amount-due-preview">
                  Billing <strong>{parseFloat(formData.bill_pct)}%</strong> of {formatCurrency(total)} —
                  <strong> Amount Due: {formatCurrency(amountDue)}</strong>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle>Line Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Header */}
                <div className="grid grid-cols-12 gap-2 text-sm font-medium text-muted-foreground">
                  <div className="col-span-5">Description</div>
                  <div className="col-span-2">Qty</div>
                  <div className="col-span-2">Rate</div>
                  <div className="col-span-1 text-right">Amount</div>
                  <div className="col-span-1 text-center">Tax</div>
                  <div className="col-span-1"></div>
                </div>

                {/* Items */}
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5">
                      <Input
                        placeholder="Item description"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        data-testid={`item-desc-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        data-testid={`item-qty-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                        data-testid={`item-price-${index}`}
                      />
                    </div>
                    <div className="col-span-1 text-right font-medium">
                      {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <Checkbox
                        checked={item.taxable !== false}
                        onCheckedChange={(checked) => handleItemChange(index, 'taxable', checked === true)}
                        data-testid={`item-taxable-${index}`}
                        aria-label="Taxable"
                      />
                    </div>
                    <div className="col-span-1 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                <Button variant="outline" onClick={addItem} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Line Item
                </Button>
                <Button
                  variant="outline"
                  onClick={addAdjustment}
                  className="w-full text-red-600 hover:text-red-700"
                  data-testid="add-adjustment-btn"
                >
                  <Minus className="h-4 w-4 mr-2" />
                  Add Adjustment / Credit
                </Button>
                <p className="text-xs text-muted-foreground">
                  Tax applies only to lines with <strong>Tax</strong> checked. Pulled estimates are tax-inclusive, so they're left unchecked.
                  Use <strong>Add Adjustment / Credit</strong> and enter a <strong>negative</strong> rate (e.g. -50) to reduce the total for work that wasn't done.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Notes to appear on invoice (e.g., '50% deposit for kitchen remodel')"
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Dates & Terms */}
          <Card>
            <CardHeader>
              <CardTitle>Invoice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, issue_date: e.target.value }))}
                />
              </div>

              <div>
                <Label>Payment Terms</Label>
                <Select
                  value={formData.terms}
                  onValueChange={(v) => setFormData((prev) => ({ ...prev, terms: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map((term) => (
                      <SelectItem key={term} value={term}>
                        {term}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Due Date (Override)</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, due_date: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave blank to calculate from terms
                </p>
              </div>

              <div>
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Totals */}
          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax ({formData.tax_rate}%)</span>
                <span>{formatCurrency(taxAmount)}</span>
              </div>
              <div className="border-t pt-3 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
              {amountDue !== total && (
                <div className="flex justify-between font-bold text-blue-700 border-t pt-3" data-testid="summary-amount-due">
                  <span>Amount Due ({parseFloat(formData.bill_pct)}%)</span>
                  <span>{formatCurrency(amountDue)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
